import { datadogLogs } from '@datadog/browser-logs';
import { Box, CardContent, Divider, Stack, Typography } from '@mui/material';
import Api, { Fare } from '@silbo/core';
import { Emoji, palette } from '@silbo/ui';
import { format } from 'date-fns';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAge } from '../../../utils/format-age';
import { getPatientEmojiIcon } from '../../../utils/get-patient-emoji-icon';
import { FareIncidents } from './FareIncidents';
import { FarePatientIsolation } from './FarePatientIsolation';
import { FareSpecificities } from './FareSpecificities';

type FareCardContentProps = {
  fare: Fare;
  otherAssignedPorters?: string;
  isDone?: boolean;
};

const FareCardContent = ({ fare, otherAssignedPorters, isDone }: FareCardContentProps) => {
  const { t } = useTranslation('patient-transport');

  const isPatientMissing = !fare.patient;
  const isGenderMissing = !isPatientMissing && !fare.patient.gender;

  // Diagnostic logging for missing patient data. Logged from an effect (once per
  // affected fare, not once per render) with a context that cannot throw, even if
  // the fare is not a genuine Parse object (e.g. deserialized from a cache).
  useEffect(() => {
    if (isPatientMissing) {
      datadogLogs.logger.error('FareCardContent: fare.patient is undefined', {
        fareId: fare.id,
        fareStatus: fare.status,
        fareReference: fare.reference,
        isArchived: fare.isArchived,
        porterUser: Api.User.current()?.id,
        isParseObject: typeof fare.getACL === 'function',
        fareACLs: typeof fare.getACL === 'function' ? fare.getACL()?.toJSON() : undefined,
      });
    } else if (isGenderMissing) {
      datadogLogs.logger.warn('FareCardContent: fare.patient.gender is undefined', {
        fareId: fare.id,
        patientId: fare.patient?.id,
        patientKeys: fare.patient ? Object.keys(fare.patient) : undefined,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPatientMissing, isGenderMissing, fare.id]);

  if (isPatientMissing) {
    return (
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography color="error">Patient data missing</Typography>
          <Typography color="text.secondary">#{fare.reference}</Typography>
        </Stack>
      </CardContent>
    );
  }

  return (
    <CardContent>
      <Stack spacing={4} divider={<Divider />}>
        <Stack direction="row" justifyContent="space-between">
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <Typography>{t('fareDetails.from')}</Typography>
              <Typography fontWeight={600}>{getOriginBreadcrumb(fare)}</Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography>{t('fareDetails.to')}</Typography>
              <Typography fontWeight={600}>{getDestinationBreadcrumb(fare)}</Typography>
            </Stack>
            <Stack direction="row" spacing={2}>
              <Typography>{t('fareDetails.at')}</Typography>
              <Typography fontWeight={600}>{fare.wantedDate ? format(fare.wantedDate, 'HH:mm') : '-'}</Typography>
            </Stack>
          </Stack>

          <Typography color="text.secondary">#{fare.reference}</Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Typography>{t('fareDetails.position')}</Typography>
          <Typography fontWeight={600}>{t(`farePositions.${fare.position}`)}</Typography>
        </Stack>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ opacity: isDone ? 0.5 : undefined, filter: isDone ? 'grayscale(1)' : undefined }}>
              <Emoji
                name={getPatientEmojiIcon(fare.patient.gender, fare.patient.age)}
                bgColor={palette.background.default}
              />
            </Box>
            <Typography fontWeight={600}>{fare.patient.fullName}</Typography>
          </Stack>
          <FarePatientIsolation fare={fare} />
          <Typography>{`${formatAge(fare.patient.birthdate)} (${format(fare.patient.birthdate, 'dd/MM/yyyy')})`}</Typography>
          <Typography variant="body2">{`${t('fareDetails.visit')} : ${fare.patient.pan}`}</Typography>
        </Stack>
        {fare.porters && fare.porters.length > 1 && (
          <Stack direction="row" spacing={2}>
            <Typography>{t('fareDetails.otherAssignedPorters')}</Typography>
            <Typography fontWeight={600}>{otherAssignedPorters}</Typography>
          </Stack>
        )}
        {fare.comment && (
          <Stack spacing={2}>
            <Typography>{t('fareDetails.comment')}</Typography>
            <Typography>{fare.comment}</Typography>
          </Stack>
        )}
        {fare.customSpecificities && fare.customSpecificities.length > 0 && (
          <FareSpecificities specificities={fare.customSpecificities} isDone={isDone} />
        )}
        {fare.incidents && fare.incidents.length > 0 && <FareIncidents incidents={fare.incidents} isDone={isDone} />}
      </Stack>
    </CardContent>
  );
};

const getOriginBreadcrumb = (fare: Fare) => {
  let from = fare.fromUnit?.name || '';

  if (fare.fromArea) {
    if (fare.fromArea.externalTypeCode === 'bed' && fare.fromArea.parent) {
      from += ` - ${fare.fromArea.parent.name}`;
    }

    from += ` - ${fare.fromArea.name}`;
  }
  return from;
};

const getDestinationBreadcrumb = (fare: Fare) => {
  let to = fare.toUnit?.name || '';

  if (fare.toArea) {
    if (fare.toArea.externalTypeCode === 'bed' && fare.toArea.parent) {
      to += ` - ${fare.toArea.parent.name}`;
    }

    to += ` - ${fare.toArea.name}`;
  }
  return to;
};

export default FareCardContent;
